import { useQueryClient } from "@tanstack/react-query";
import { Button, Space, Switch, Typography } from "antd";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { ExportModal } from "~components/ExportModal/ExportModal";
import { ImportModal } from "~components/ImportButton/ImportModal.tsx";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { defaultFixturesExportFieldNames, fixturesExportFields } from "~pages/Fixtures/FixtureListPage/constants/exportFields";
import { useExportFixtures } from "~pages/Fixtures/FixtureListPage/hooks/useExportFixtures.ts";
import { useImportFixtures } from "~pages/Fixtures/FixtureListPage/hooks/useImportFixtures.tsx";
import PermissionCheck from "../../../Common/PermissionCheck";
import { FixtureFilters, FixtureList, UpsertFixtureModal } from "./components";
import { useFetchAllSeasonFixtures, useFilterFixtures } from "./hooks";
import { FilterTypes } from "./types";
import "./FixtureListPageStyles.scss";

const PermissionName = PermissionNames.FIXTURE;

const FixtureListPage = () => {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const sportConfiguration = useSportConfiguration();
  const { data: serverFixtures, isLoading } = useFetchAllSeasonFixtures();
  const [currentFilter, setCurrentFilter] = useState<any>({});
  const clientFixtures = useFilterFixtures(serverFixtures as any, currentFilter);
  const [massEditMode, setMassEditMode] = useState<boolean>(false);
  const [isNewFixtureModelOpened, setIsNewFixtureModelOpened] = useState<boolean>(false);

  const onFilterChangedHandler = useCallback((filterType: FilterTypes, values: string[]) => {
    setCurrentFilter((previousFilter: any) => ({
      ...previousFilter,
      [FilterTypes[filterType]]: values,
    }));
  }, []);

  const onMassEditModeChangedHandler = useCallback(() => {
    setMassEditMode((prevChecked) => !prevChecked);
    return queryClient.invalidateQueries({ queryKey: ["fixtureListPage"] });
  }, [queryClient]);

  const onNewFixtureButtonClickedHandler = useCallback(() => setIsNewFixtureModelOpened((prevState) => !prevState), []);

  const onRefreshButtonClickedHandler = useCallback(() => queryClient.invalidateQueries({ queryKey: ["fixtureListPage"] }), [queryClient]);

  const { mutateAsync: startImporting } = useImportFixtures();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportFixtures();

  return (
    <div className="fixtures-page-grid">
      <Space className="fixtures-page-controls">
        <Typography.Title level={4}>{formatMessage({ id: "fixtureListPage.title" })}</Typography.Title>
        <Space>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Space>
              {formatMessage({ id: "edit.mode" })}
              <Switch onChange={onMassEditModeChangedHandler} />
            </Space>
          </PermissionCheck>
          <div>
            <Button
              onClick={() => onRefreshButtonClickedHandler()}
              type={"text"}
              size={"small"}
              title={formatMessage({ id: "fixtureListPage.button.refresh" })}
              icon={<i className={"fas fa-arrows-rotate"} />}
            />
          </div>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE, PermissionOperations.UPDATE]}>
            <ImportModal
              title={formatMessage({ id: "import.modal.title.fixtures" })}
              description={formatMessage({ id: "import.modal.text.fixtures" })}
              startImporting={startImporting}
            />
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.READ]}>
            <ExportModal
              handleExport={startExport}
              defaultFields={defaultFixturesExportFieldNames}
              groupedFields={fixturesExportFields}
              localStorageKey={"export.fixtures"}
              loading={isExportPending}
            />
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
            <Button onClick={onNewFixtureButtonClickedHandler} icon={<i className="fa-solid fa-plus mr-1" />} type={"primary"}>
              {formatMessage({ id: "fixtureListPage.button.addNewFixture" })}
            </Button>
            {isNewFixtureModelOpened && <UpsertFixtureModal onClose={() => setIsNewFixtureModelOpened(false)} />}
          </PermissionCheck>
        </Space>
      </Space>

      {!isLoading && (
        <FixtureFilters
          competitorType={sportConfiguration.competitorType}
          serverFixtures={serverFixtures as any}
          onFilterChange={onFilterChangedHandler}
          currentFilter={currentFilter}
        />
      )}
      <FixtureList
        competitorType={sportConfiguration.competitorType}
        clientFixtures={clientFixtures}
        isLoading={isLoading}
        massEditMode={massEditMode}
      />
    </div>
  );
};

export default FixtureListPage;
